// Colloquial phrasing → underwriting condition lookup.
//
// Agents (and clients) talk like humans, not like underwriters. This
// map captures the way conditions actually show up in a kitchen-table
// conversation and resolves them to the canonical condition names used
// in the FEX + Term underwriting grids.
//
// Example: "client lost both legs" → Amputation
//          "on oxygen all the time" → Oxygen
//          "mini stroke 6 months ago" → Stroke / TIA
//
// Each entry has:
//   phrases:    array of substrings (case-insensitive). Match if any
//               phrase is found in the input.
//   conditions: name fragments that get matched against the active
//               condition grid (FEX_CONDITIONS.name or
//               TERM_CONDITIONS.name) via case-insensitive substring.
//
// Multiple aliases can fire from one paragraph of input. Each match
// surfaces a "🔎 Reading 'X' as: Y" hint in the placement view so the
// agent sees how their words got interpreted.

export const PHRASE_ALIASES = [
  // ── Amputation ──────────────────────────────────────────────────
  { phrases: ['lost a leg', 'lost both legs', 'lost an arm', 'lost both arms', 'missing limb', 'missing leg', 'amputee', 'amputated', 'prosthetic', 'prosthesis', 'fake leg', 'fake arm'], conditions: ['Amputation'] },

  // ── Oxygen ──────────────────────────────────────────────────────
  { phrases: ['on oxygen', 'oxygen tank', 'oxygen machine', 'oxygen at home', 'home oxygen', 'using oxygen'], conditions: ['Oxygen'] },

  // ── Stroke / TIA ────────────────────────────────────────────────
  { phrases: ['mini stroke', 'mini-stroke', 'small stroke', 'had a stroke', 'stroke last', 'TIA', 'transient ischemic', 'cerebrovascular'], conditions: ['Stroke', 'TIA'] },

  // ── Dementia / Alzheimer's ──────────────────────────────────────
  { phrases: ['memory loss', 'losing memory', 'losing his memory', 'losing her memory', 'forgetful', 'forgets things', 'senile', 'senility', 'cognitive decline', 'foggy brain', 'mental decline', 'dementia', "alzheimer", "alzheimer's"], conditions: ['Alzheimer', 'Dementia', 'Memory'] },

  // ── Assisted Living / LTC ───────────────────────────────────────
  { phrases: ['nursing home', 'assisted living', 'long-term care', 'long term care', 'memory care', 'old folks home', 'care facility', 'in a facility', 'rest home'], conditions: ['Assisted Living'] },

  // ── Diabetes ────────────────────────────────────────────────────
  { phrases: ['takes insulin', 'on insulin', 'using insulin', 'shoots insulin', 'high blood sugar', 'sugar diabetes', 'sugar problem', 'diabetic', 'type 1', 'type 2', 'A1C', 'A1c', 'glucose'], conditions: ['Diabetes'] },

  // ── Diabetic Complications ──────────────────────────────────────
  { phrases: ['diabetic neuropathy', 'diabetic feet', 'lost feeling in feet', 'numbness in feet', 'tingling feet', 'kidney damage from diabetes', 'retinopathy'], conditions: ['Diabetic Complication', 'Neuropathy'] },

  // ── CHF ─────────────────────────────────────────────────────────
  { phrases: ['heart failure', 'CHF', 'congestive heart', 'fluid around heart', 'fluid around the heart', 'fluid in the lungs', 'enlarged heart', 'weak heart'], conditions: ['Congestive Heart Failure'] },

  // ── Wheelchair / mobility ───────────────────────────────────────
  { phrases: ["can't walk", 'cant walk', 'cannot walk', 'bedridden', 'paralyzed', 'paralysis', 'paraplegic', 'quadriplegic', 'scooter', 'wheelchair', 'wheel chair', 'electric scooter', 'mobility aid', 'mobility cart'], conditions: ['Wheelchair', 'Paraplegia', 'Quadriplegia'] },

  // ── Walker / cane ───────────────────────────────────────────────
  { phrases: ['uses a walker', 'walker', 'walks with cane', 'uses cane', 'uses a cane'], conditions: ['Walker', 'Wheelchair'] },

  // ── COPD / breathing ────────────────────────────────────────────
  { phrases: ['emphysema', 'chronic bronchitis', 'COPD', 'breathing problem', 'shortness of breath', 'inhaler all day', 'on inhaler', 'puffer', 'lung disease', 'damaged lungs'], conditions: ['COPD', 'Emphysema'] },

  // ── Asthma ──────────────────────────────────────────────────────
  { phrases: ['asthma', 'wheezing', 'asthma attack', 'rescue inhaler'], conditions: ['Asthma'] },

  // ── Cancer / tumor / chemo ──────────────────────────────────────
  { phrases: ['cancer', 'tumor', 'chemo', 'chemotherapy', 'radiation', 'oncology', 'malignant', 'lymphoma', 'leukemia', 'cancer treatment', 'tumor removed'], conditions: ['Cancer', 'Tumor'] },

  // ── Melanoma ────────────────────────────────────────────────────
  { phrases: ['melanoma', 'skin cancer'], conditions: ['Melanoma', 'Cancer'] },

  // ── Pacemaker / Defibrillator ───────────────────────────────────
  { phrases: ['pacemaker', 'defibrillator', 'ICD', 'heart device', 'heart implant', 'AICD'], conditions: ['Pacemaker', 'Defibrillator'] },

  // ── Stent ───────────────────────────────────────────────────────
  { phrases: ['stent', 'stents', 'cardiac stent', 'heart stent', 'stent put in'], conditions: ['Stent'] },

  // ── Heart attack ────────────────────────────────────────────────
  { phrases: ['heart attack', 'had an MI', 'myocardial infarction', 'cardiac arrest', 'widow maker'], conditions: ['Heart Attack'] },

  // ── Angina ──────────────────────────────────────────────────────
  { phrases: ['angina', 'chest pain', 'chest pains'], conditions: ['Angina'] },

  // ── AFib ────────────────────────────────────────────────────────
  { phrases: ['afib', 'a fib', 'a-fib', 'atrial fibrillation', 'irregular heartbeat', 'arrhythmia'], conditions: ['AFib', 'Arrhythmia', 'Atrial Fibrillation'] },

  // ── Heart Surgery ───────────────────────────────────────────────
  { phrases: ['open heart surgery', 'bypass surgery', 'CABG', 'heart bypass', 'heart valve', 'valve replacement'], conditions: ['Heart Surgery', 'Heart Valve'] },

  // ── Neuropathy ──────────────────────────────────────────────────
  { phrases: ['neuropathy', 'nerve damage', 'nerve pain'], conditions: ['Neuropathy'] },

  // ── Terminal / Hospice ──────────────────────────────────────────
  { phrases: ['terminal', 'hospice', 'six months to live', '6 months to live', 'end of life', 'palliative', 'doctor gave him', 'doctor gave her', 'doesnt have long', "doesn't have long"], conditions: ['Terminal', 'Hospice'] },

  // ── Kidney / Dialysis ───────────────────────────────────────────
  { phrases: ['dialysis', 'kidney failure', 'on dialysis', 'kidney transplant', 'bad kidneys', 'kidney disease'], conditions: ['Dialysis', 'Kidney', 'Renal'] },

  // ── Liver / Cirrhosis ───────────────────────────────────────────
  { phrases: ['cirrhosis', 'liver damage', 'scarred liver', 'fatty liver', 'liver disease', 'bad liver'], conditions: ['Cirrhosis', 'Liver'] },

  // ── Hepatitis ───────────────────────────────────────────────────
  { phrases: ['hep c', 'hepatitis c', 'hep b', 'hepatitis b', 'hepatitis a'], conditions: ['Hepatitis'] },

  // ── Mental health ───────────────────────────────────────────────
  { phrases: ['depression', 'depressed', 'antidepressants', 'SSRI'], conditions: ['Depression'] },
  { phrases: ['bipolar', 'manic depressive', 'mania', 'manic episodes'], conditions: ['Bipolar'] },
  { phrases: ['schizophrenia', 'schizo', 'hearing voices', 'psychotic'], conditions: ['Schizophrenia'] },
  { phrases: ['anxiety', 'anxious', 'panic attacks', 'panic disorder', 'GAD'], conditions: ['Anxiety'] },
  { phrases: ['PTSD', 'post-traumatic', 'post traumatic'], conditions: ['PTSD'] },

  // ── Neuro ───────────────────────────────────────────────────────
  { phrases: ['multiple sclerosis', "M.S.", "MS diagnosis", 'demyelinating'], conditions: ['Multiple Sclerosis'] },
  { phrases: ["parkinsons", "parkinson's", "parkinson", 'tremors', 'shaking hands'], conditions: ['Parkinson'] },
  { phrases: ['ALS', "lou gehrig"], conditions: ['ALS'] },
  { phrases: ['huntingtons', "huntington's"], conditions: ['Huntington'] },
  { phrases: ['seizure', 'seizures', 'epilepsy', 'epileptic', 'grand mal'], conditions: ['Seizure', 'Epilepsy'] },

  // ── Sleep apnea ─────────────────────────────────────────────────
  { phrases: ['sleep apnea', 'cpap', 'bipap', 'sleep machine', 'snore loud', 'snores loud'], conditions: ['Sleep Apnea'] },

  // ── Felony / DUI ────────────────────────────────────────────────
  { phrases: ['felony', 'felon', 'in jail', 'in prison', 'incarcerated', 'just got out', 'paroled', 'on parole', 'on probation'], conditions: ['Felony', 'Jail', 'Parole', 'Incarcerated'] },
  { phrases: ['DUI', 'DWI', 'drunk driving', 'driving drunk', 'got a DUI'], conditions: ['DUI'] },

  // ── Drug/alcohol ────────────────────────────────────────────────
  { phrases: ['rehab', 'addiction', 'alcoholic', 'alcoholism', 'detox', 'AA meetings'], conditions: ['Alcohol', 'Drug Abuse', 'Drug Use'] },
  { phrases: ['cocaine', 'heroin', 'meth', 'methamphetamine', 'fentanyl', 'illegal drugs', 'street drugs'], conditions: ['Illegal Drugs', 'Drug Use'] },
  { phrases: ['weed', 'marijuana', 'cannabis', 'pot smoker', 'smokes pot'], conditions: ['Marijuana'] },

  // ── Tobacco ─────────────────────────────────────────────────────
  { phrases: ['smoker', 'smokes', 'cigarettes', 'vapes', 'chewing tobacco', 'dip', 'nicotine'], conditions: ['Smoker', 'Nicotine', 'Tobacco'] },

  // ── Arthritis ───────────────────────────────────────────────────
  { phrases: ['rheumatoid', 'RA arthritis', 'autoimmune arthritis'], conditions: ['Rheumatoid'] },
  { phrases: ['arthritis', 'joint pain'], conditions: ['Arthritis'] },

  // ── GI ──────────────────────────────────────────────────────────
  { phrases: ["crohn's", 'crohns', 'IBD', 'inflammatory bowel'], conditions: ["Crohn"] },
  { phrases: ['ulcerative colitis', 'colitis'], conditions: ['Ulcerative Colitis'] },

  // ── Autoimmune / Lupus ──────────────────────────────────────────
  { phrases: ['lupus', 'SLE'], conditions: ['Lupus'] },
  { phrases: ['autoimmune'], conditions: ['Autoimmune', 'Lupus'] },
  { phrases: ['sarcoidosis'], conditions: ['Sarcoidosis'] },

  // ── HBP ─────────────────────────────────────────────────────────
  { phrases: ['high blood pressure', 'hypertension', 'HBP', 'BP medication'], conditions: ['High Blood Pressure'] },

  // ── Sickle cell ─────────────────────────────────────────────────
  { phrases: ['sickle cell'], conditions: ['Sickle Cell'] },

  // ── Aneurysm ────────────────────────────────────────────────────
  { phrases: ['aneurysm', 'brain aneurysm', 'aortic aneurysm'], conditions: ['Aneurysm'] },

  // ── Bone marrow / Transplant ────────────────────────────────────
  { phrases: ['bone marrow transplant'], conditions: ['Bone Marrow Transplant'] },
  { phrases: ['organ transplant', 'kidney transplant', 'liver transplant', 'heart transplant'], conditions: ['Transplant'] },

  // ── Down's / Autism ─────────────────────────────────────────────
  { phrases: ["down syndrome", "down's syndrome", 'downs syndrome', 'trisomy 21'], conditions: ["Down"] },
  { phrases: ['autism', 'autistic', 'ASD', 'on the spectrum'], conditions: ['Autism'] },

  // ── TB / Cystic Fibrosis ────────────────────────────────────────
  { phrases: ['tuberculosis', 'TB'], conditions: ['Tuberculosis'] },
  { phrases: ['cystic fibrosis', 'CF'], conditions: ['Cystic Fibrosis'] },

  // ── Black lung ──────────────────────────────────────────────────
  { phrases: ['black lung', 'coal miner lung'], conditions: ['Black Lung'] },

  // ── Cerebral palsy ──────────────────────────────────────────────
  { phrases: ['cerebral palsy', 'CP'], conditions: ['Cerebral Palsy'] },

  // ── Chronic pain ────────────────────────────────────────────────
  { phrases: ['chronic pain', 'severe pain', 'opioid', 'on pain pills', 'taking pain meds'], conditions: ['Chronic Pain', 'Pain Medication'] },

  // ── Coronary Artery Disease ─────────────────────────────────────
  { phrases: ['coronary artery', 'blocked artery', 'arterial blockage', 'clogged arteries'], conditions: ['Coronary Artery'] },

  // ── PAD / PVD ───────────────────────────────────────────────────
  { phrases: ['PAD', 'peripheral artery', 'peripheral vascular', 'PVD', 'poor circulation'], conditions: ['PAD', 'PVD'] },

  // ── Blood clots / DVT ───────────────────────────────────────────
  { phrases: ['blood clot', 'blood clots', 'DVT', 'pulmonary embolism', 'PE'], conditions: ['Blood Clots'] },

  // ── Pulmonary Fibrosis ──────────────────────────────────────────
  { phrases: ['pulmonary fibrosis', 'lung scarring', 'scarred lungs', 'IPF'], conditions: ['Pulmonary Fibrosis'] },

  // ── Cardiomyopathy ──────────────────────────────────────────────
  { phrases: ['cardiomyopathy', 'enlarged heart muscle'], conditions: ['Cardiomyopathy'] },
];

// Resolve every phrase match in the agent's input. Returns an array
// of { matched: string, conditionFragments: string[] } entries — one
// per alias that hit. The placement view uses these to (a) auto-toggle
// matching condition chips and (b) show "🔎 Reading 'X' as: Y" hints.
export function resolvePhraseAliases(text) {
  const lower = String(text || '').toLowerCase();
  if (!lower.trim()) return [];
  const matches = [];
  const seen = new Set();
  for (const alias of PHRASE_ALIASES) {
    for (const phrase of alias.phrases) {
      const p = phrase.toLowerCase();
      if (lower.includes(p) && !seen.has(p)) {
        seen.add(p);
        matches.push({ matched: phrase, conditionFragments: alias.conditions });
        break; // one match per alias group is enough
      }
    }
  }
  return matches;
}

// Canonical "underwriting decline" set for HEALTH products (STM,
// TriTerm, HPG, indemnity SI variants). Anything that matches one of
// these condition fragments forces the GI/ACA path in the recommender.
// Drawn from the FTPIG strategy doc plus common-sense underwriting:
// cancer (active), insulin-dep diabetes, dialysis, recent MI/stroke,
// MS/ALS/Parkinson, seizure disorder, COPD on home O2, schizophrenia,
// bipolar w/ hospitalization, HIV/AIDS, Hep B/C, terminal/hospice,
// wheelchair-bound, transplant.
export const HEALTH_DISQUALIFIERS = [
  'Cancer', 'Tumor', 'Chemo', 'Melanoma',
  'Diabetes',                          // simplification; insulin flag is below
  'Dialysis', 'Kidney', 'Renal',
  'Heart Attack', 'Stroke', 'TIA',
  'Multiple Sclerosis', 'ALS', 'Parkinson', 'Huntington',
  'Seizure', 'Epilepsy',
  'Oxygen', 'COPD', 'Emphysema',
  'Schizophrenia', 'Bipolar',
  'Hepatitis',
  'Terminal', 'Hospice',
  'Wheelchair', 'Paraplegia', 'Quadriplegia',
  'Pacemaker', 'Defibrillator', 'AICD',
  'Congestive Heart Failure', 'Heart Surgery', 'Heart Valve',
  'Cirrhosis', 'Liver',
  'Alzheimer', 'Dementia',
  'Amputation',
];

// Categorize condition fragments into discrete bins so the UI can
// surface them as chips. e.g. 'Cancer' → 'oncology', 'Diabetes' →
// 'metabolic', 'Heart Attack' → 'cardiac'.
export const CONDITION_BUCKETS = {
  oncology:   ['Cancer', 'Tumor', 'Chemo', 'Melanoma'],
  metabolic:  ['Diabetes'],
  renal:      ['Dialysis', 'Kidney', 'Renal'],
  cardiac:    ['Heart Attack', 'Stroke', 'TIA', 'Pacemaker', 'Defibrillator', 'AICD', 'Stent', 'Angina', 'AFib', 'Arrhythmia', 'Atrial Fibrillation', 'Heart Surgery', 'Heart Valve', 'Congestive Heart Failure'],
  neuro:      ['Multiple Sclerosis', 'ALS', 'Parkinson', 'Huntington', 'Seizure', 'Epilepsy', 'Neuropathy', 'Diabetic Complication'],
  pulmonary:  ['Oxygen', 'COPD', 'Emphysema', 'Asthma', 'Sleep Apnea'],
  mental:     ['Schizophrenia', 'Bipolar', 'Depression', 'Anxiety', 'PTSD'],
  infectious: ['Hepatitis'],
  terminal:   ['Terminal', 'Hospice'],
  mobility:   ['Wheelchair', 'Paraplegia', 'Quadriplegia', 'Walker', 'Amputation'],
  hepatic:    ['Cirrhosis', 'Liver'],
  cognitive:  ['Alzheimer', 'Dementia', 'Memory'],
  ltc:        ['Assisted Living'],
  legal:      ['Felony', 'Jail', 'Parole', 'Incarcerated', 'DUI'],
};

// Assess a free-text paragraph for HEALTH insurability. Returns:
//   { insurable, hits[], disqualifiers[], buckets[], advice }
// hits = full resolver matches (so the UI can show "🔎 Reading X as Y")
// disqualifiers = subset of condition fragments that force GI path
// buckets = unique CONDITION_BUCKETS keys, for chip rendering
export function assessHealthInsurability(text) {
  const hits = resolvePhraseAliases(text);
  const allFragments = new Set();
  hits.forEach(h => h.conditionFragments.forEach(f => allFragments.add(f)));

  const disqualifiers = [];
  HEALTH_DISQUALIFIERS.forEach(d => {
    const dl = d.toLowerCase();
    allFragments.forEach(f => {
      if (String(f).toLowerCase().includes(dl) || dl.includes(String(f).toLowerCase())) {
        if (!disqualifiers.includes(d)) disqualifiers.push(d);
      }
    });
  });

  const buckets = new Set();
  Object.entries(CONDITION_BUCKETS).forEach(([bucket, frags]) => {
    frags.forEach(f => {
      allFragments.forEach(g => {
        if (String(g).toLowerCase().includes(f.toLowerCase()) || f.toLowerCase().includes(String(g).toLowerCase())) {
          buckets.add(bucket);
        }
      });
    });
  });

  const insurable = disqualifiers.length === 0;
  let advice;
  if (!text || !text.trim()) advice = 'No conditions disclosed yet. Default insurability assumption: insurable.';
  else if (insurable)        advice = 'No disqualifying conditions detected. Healthy / insurable path — STM, TriTerm, HPG are on the table.';
  else                       advice = `Disqualifying condition${disqualifiers.length === 1 ? '' : 's'} detected: ${disqualifiers.join(', ')}. Recommend ACA + GI indemnity stack (HospitalWise Bundle 2 + AccidentWise GI).`;

  return {
    insurable,
    hits,
    disqualifiers,
    buckets: Array.from(buckets),
    advice,
  };
}

// Given a resolver hit + a list of condition names (e.g. FEX_CONDITIONS
// or TERM_CONDITIONS .name), return the indices that match.
export function indicesForFragments(fragments, conditionNames) {
  const out = new Set();
  for (const frag of fragments) {
    const f = String(frag).toLowerCase();
    conditionNames.forEach((name, idx) => {
      if (name.toLowerCase().includes(f)) out.add(idx);
    });
  }
  return Array.from(out);
}
